<script setup>
import ReportIRORepository from '../../repository/ReportIRORepository.js'
import IroList from './IroList.vue'
import EntitySpecificIroList from './EntitySpecificIroList.vue'
import Stats from './Stats.vue'
import { BreadcrumbContainer, Breadcrumbs, TabButton, TabContent, TableSkeletonLoader, Tabs, useToasterInSetup } from 'dmui_components'
import { computed, ref, onUnmounted, watch } from 'vue'

import permissionChecker from '@/components/permissionChecker'
const { hasPermission } = permissionChecker()
const readonly = computed(() => !hasPermission('reports', 'wd'))

const props = defineProps({
	reportId: {
		type: Number,
		default: undefined
	},
	permission: {
		type: Array,
		default: () => []
	}
})

const iroData = ref()
const loading = ref(false)

const toaster = useToasterInSetup({ page: 'EditReportIro' }, onUnmounted)

const rootBreadcrumb = [{ title: 'IRO', icon: 'uil-arrow-left', path: '/document/report', query: { tabs: 'iro' } }]

watch(
	() => props.reportId,
	async (newVal) => {
		if (newVal) {
			try {
				loading.value = true
				iroData.value = await ReportIRORepository.getReportIROs(newVal)
			} catch (error) {
				console.error('Error while fetching report IROs', error)
				toaster.show('Failed to get report IROs. Please try again.')
			} finally {
				loading.value = false
			}
		}
	},
	{ immediate: true }
)

async function deleteIro(id) {
	try {
		await ReportIRORepository.deleteOneIro(id)
		iroData.value = await ReportIRORepository.getReportIROs(props.reportId)
	} catch (e) {
		console.error(e)
	}
}

async function deleteEntitySpecificIro(id) {
	try {
		await ReportIRORepository.deleteOneEntitySpecificIro(id)
		iroData.value = await ReportIRORepository.getReportIROs(props.reportId)
	} catch (e) {
		console.error(e)
	}
}
</script>

<template>
	<BreadcrumbContainer>
		<Breadcrumbs :data="[...rootBreadcrumb]" />
	</BreadcrumbContainer>
	<div class="content">
		<div v-if="iroData" class="content__top">
			<div class="header">
				<h2 class="page-header">{{ iroData.org_name }}</h2>
				<div class="sub-header">
					<span class="primary-header semi-bold">{{ iroData.document_type }} CSRD </span>
					<span class="primary-header light">{{ iroData.document_year }}</span>
				</div>
			</div>
		</div>

		<Tabs default="csrd-iros">
			<template #buttons>
				<TabButton name="csrd-iros">CSRD IROs</TabButton>
				<TabButton name="entity-specific-iros">Entity-specific IROs</TabButton>
			</template>
			<TabContent name="csrd-iros">
				<Stats :iros="iroData?.iros" />
				<IroList :iros="iroData?.iros" :readonly="readonly" @delete="deleteIro">
					<TableSkeletonLoader v-if="loading" :n-rows="20" :n-columns="7" />
				</IroList>
			</TabContent>
			<TabContent name="entity-specific-iros">
				<Stats :iros="iroData?.entity_specific_iros" />
				<EntitySpecificIroList :iros="iroData?.entity_specific_iros" :readonly="readonly" @delete="deleteEntitySpecificIro">
					<TableSkeletonLoader v-if="loading" :n-rows="20" :n-columns="6" />
				</EntitySpecificIroList>
			</TabContent>
		</Tabs>
	</div>
</template>
<style scoped lang="less">
.content {
	display: flex;
	flex-direction: column;
	gap: @medium;
	margin-top: @huge;

	.content__top {
		display: flex;
		flex-direction: column;
		gap: @medium;
	}
}

.sub-header {
	text-transform: capitalize;
	margin-top: @micro;
}

// TODO: remove once new breadcrumb is deployed
.large-screen-with-menu {
	left: auto !important;
}
.breadcrumb-container {
	padding: @micro;
	min-height: @micro;
}
</style>
